import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PIVONY_LOGO_ON_DARK_BG } from "../Login/logos";
import {
  getSignupProductByKey,
  isPaidSignupProduct,
} from "./signupProducts";
import styles from "./signupLeftPanel.module.scss";

const PRODUCT_ICONS = {
  survey: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H5v-2h7v2zm7-4H5v-2h14v2zm0-4H5V7h14v2z" />
    </svg>
  ),
  sonic_prospect: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3a4 4 0 0 0-4 4v4a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4zm6 8c0 3.31-2.69 6-6 6s-6-2.69-6-6H4c0 3.93 2.61 7.23 6.17 8.32V22h3.66v-2.68C17.39 18.23 20 14.93 20 11h-2z" />
    </svg>
  ),
  advisor: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
    </svg>
  ),
  default: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2L2 7l10 5 10-5-10-5zm0 9l-10-5v10l10 5 10-5V6l-10 5z" />
    </svg>
  ),
};

const SignupLeftPanel = ({ intentProductKey = null }) => {
  const { t } = useTranslation();
  const paidIntent =
    Boolean(intentProductKey) && isPaidSignupProduct(intentProductKey);
  const intentMeta = intentProductKey
    ? getSignupProductByKey(intentProductKey)
    : null;
  const intentLabel = intentMeta ? t(intentMeta.labelKey) : "";
  const intentDesc = intentMeta ? t(intentMeta.descKey) : "";

  const headlines = useMemo(() => {
    if (paidIntent) {
      return [t("signup_hero.paid.headline", { product: intentLabel })];
    }
    if (intentProductKey === "advisor") {
      return [t("signup_hero.advisor_headline")];
    }
    if (
      intentProductKey === "sonic_prospect" ||
      intentProductKey === "sonic_prospect_channels" ||
      intentProductKey === "sonic_prospect_ecommerce"
    ) {
      return [t("signup_hero.prospect_headline")];
    }
    return [
      t("signup_hero.headline_1"),
      t("signup_hero.headline_2"),
      t("signup_hero.headline_3"),
    ];
  }, [t, paidIntent, intentLabel, intentProductKey]);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    if (paidIntent || headlines.length <= 1) {
      setHeadlineIndex(0);
      setFadeIn(true);
      return undefined;
    }
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setHeadlineIndex((i) => (i + 1) % headlines.length);
        setFadeIn(true);
      }, 450);
    }, 3200);
    return () => clearInterval(interval);
  }, [headlines.length, paidIntent]);

  const products = useMemo(() => {
    if (paidIntent && intentMeta) {
      return [
        {
          key: intentMeta.key,
          title: intentLabel,
          desc: intentDesc,
          badge: t("signup_hero.paid.badge"),
          className: styles.cardPaid,
          icon: PRODUCT_ICONS[intentMeta.key] || PRODUCT_ICONS.default,
        },
      ];
    }
    if (intentProductKey === "advisor" && intentMeta) {
      return [
        {
          key: "advisor",
          title: intentLabel,
          desc: t("signup_hero.advisor_blurb"),
          badge: t("signup_hero.free_with_data"),
          className: styles.cardSurvey,
          icon: PRODUCT_ICONS.advisor,
        },
      ];
    }
    if (
      (intentProductKey === "sonic_prospect" ||
        intentProductKey === "sonic_prospect_channels" ||
        intentProductKey === "sonic_prospect_ecommerce") &&
      intentMeta
    ) {
      return [
        {
          key: "sonic_prospect",
          title: intentLabel,
          desc: intentDesc || t("signup_hero.sonic_blurb"),
          badge: t("signup_products.start_now"),
          className: styles.cardSonic,
          icon: PRODUCT_ICONS.sonic_prospect,
        },
      ];
    }
    return [
      {
        key: "survey",
        title: t("signup_products.survey.label"),
        desc: t("signup_hero.survey_blurb"),
        badge: t("signup_hero.always_free"),
        className: styles.cardSurvey,
        icon: PRODUCT_ICONS.survey,
      },
      {
        key: "sonic_prospect",
        title: t("signup_products.sonic_prospect.label"),
        desc: t("signup_hero.sonic_blurb"),
        badge: t("signup_products.sonic_prospect.badge"),
        className: styles.cardSonic,
        icon: PRODUCT_ICONS.sonic_prospect,
      },
    ];
  }, [t, paidIntent, intentMeta, intentLabel, intentDesc, intentProductKey]);

  const pills = useMemo(() => {
    if (paidIntent) {
      return [
        t("signup_hero.paid.pill_sales"),
        t("signup_hero.paid.pill_trial"),
        t("signup_hero.paid.pill_setup"),
      ];
    }
    if (intentProductKey === "advisor") {
      return [
        t("signup_hero.pill_free_with_data"),
        t("signup_hero.pill_setup"),
        t("signup_hero.pill_audience"),
      ];
    }
    if (
      intentProductKey === "sonic_prospect" ||
      intentProductKey === "sonic_prospect_channels" ||
      intentProductKey === "sonic_prospect_ecommerce"
    ) {
      return [
        t("signup_hero.pill_connect_site"),
        t("signup_hero.pill_pay_per_qp"),
        t("signup_hero.pill_launch_rate"),
      ];
    }
    return [
      t("signup_hero.pill_free_forever"),
      t("signup_products.start_now"),
      t("signup_hero.pill_audience"),
    ];
  }, [t, paidIntent, intentProductKey]);

  const subtext =
    paidIntent
      ? t("signup_hero.paid.subtext", { product: intentLabel })
      : intentProductKey === "advisor"
        ? t("signup_hero.advisor_subtext")
        : intentProductKey === "sonic_prospect" ||
            intentProductKey === "sonic_prospect_channels" ||
            intentProductKey === "sonic_prospect_ecommerce"
          ? t("signup_hero.prospect_subtext")
          : t("signup_hero.subtext");

  return (
    <aside className={styles.panelLeft}>
      <div className={styles.brandRow}>
        <img
          src={PIVONY_LOGO_ON_DARK_BG}
          alt="Pivony"
          className={styles.brandLogo}
        />
      </div>

      <div className={styles.hero}>
        <h1
          className={`${styles.headline} ${
            fadeIn ? styles.headlineVisible : styles.headlineHidden
          }`}
        >
          {headlines[headlineIndex]}
        </h1>
        <p className={styles.subtext}>{subtext}</p>

        <div className={styles.productStage} aria-hidden="true">
          <div className={styles.glow} />
          {products.map((product, index) => (
            <div
              key={product.key}
              className={`${styles.productCard} ${product.className}`}
              style={{ animationDelay: `${index * 0.35}s` }}
            >
              <div className={styles.productIcon}>{product.icon}</div>
              <div className={styles.productBody}>
                <div className={styles.productTitleRow}>
                  <span className={styles.productTitle}>{product.title}</span>
                  <span className={styles.productBadge}>{product.badge}</span>
                </div>
                <p className={styles.productDesc}>{product.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.pillsRow}>
          {pills.map((pill) => (
            <span key={pill} className={styles.pill}>
              {pill}
            </span>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default SignupLeftPanel;
